import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/plugins/api'
import type { PlatformUser } from '@/shared/types/models'
import type { ApiResponse } from '@/shared/types/api'
import { verify2FA as verify2FAService, type TwoFactorMethod } from '@/shared/services/auth'

export interface LoginChallenge {
  challenge_token: string
  expires_in: number
  /** 'totp' = app authenticator | 'email' = OTP envoye par mail. */
  method: TwoFactorMethod
}

export const useAuthStore = defineStore('admin-auth', () => {
  const user = ref<PlatformUser | null>(null)
  const loading = ref(false)
  const pendingChallenge = ref<LoginChallenge | null>(null)

  const isAuthenticated = computed(() => !!user.value)
  const isOwner = computed(() => !!user.value?.is_owner)
  const fullName = computed(() =>
    user.value ? `${user.value.first_name} ${user.value.last_name}`.trim() : '',
  )
  const permissionCodes = computed(() => new Set(user.value?.permission_codes ?? []))

  function can(module: string, action: string): boolean {
    if (!user.value) return false
    if (user.value.is_owner) return true
    return permissionCodes.value.has(`${module}:${action}`)
      || permissionCodes.value.has(`${module}:manage`)
  }

  async function login(email: string, password: string): Promise<{ requires2fa: boolean }> {
    loading.value = true
    try {
      const { data } = await api.post<ApiResponse<{
        user?: PlatformUser
        requires_2fa?: boolean
        method?: TwoFactorMethod
        challenge_token?: string
        expires_in?: number
      }>>(
        '/api/admin/auth/login',
        { email, password },
      )
      const payload = data.data
      if (payload.requires_2fa) {
        pendingChallenge.value = {
          challenge_token: payload.challenge_token!,
          expires_in: payload.expires_in ?? 300,
          method: payload.method ?? 'totp',
        }
        return { requires2fa: true }
      }
      pendingChallenge.value = null
      // Le login ne retourne pas les permissions — on les récupère via /me.
      await fetchMe()
      return { requires2fa: false }
    } finally {
      loading.value = false
    }
  }

  async function verify2FA(code: string): Promise<{
    used_backup_code: boolean
    remaining_backup_codes: number
  }> {
    if (!pendingChallenge.value) throw new Error('Aucun challenge 2FA en cours')
    loading.value = true
    try {
      const res = await verify2FAService(pendingChallenge.value.challenge_token, code)
      pendingChallenge.value = null
      await fetchMe()
      return {
        used_backup_code: res.used_backup_code,
        remaining_backup_codes: res.remaining_backup_codes,
      }
    } finally {
      loading.value = false
    }
  }

  function cancelChallenge() {
    pendingChallenge.value = null
  }

  async function fetchMe() {
    loading.value = true
    try {
      const { data } = await api.get<ApiResponse<{ user: PlatformUser; permissions?: string[] }>>('/api/admin/auth/me')
      const payload = data.data
      // L'endpoint /me retourne {user, permissions} — on aplatit permission_codes sur le user.
      const merged: PlatformUser = {
        ...payload.user,
        permission_codes: payload.permissions ?? payload.user.permission_codes ?? [],
      }
      user.value = merged
    } catch {
      user.value = null
    } finally {
      loading.value = false
    }
  }

  function clearAuth() {
    user.value = null
    pendingChallenge.value = null
  }

  async function logout() {
    try {
      await api.post('/api/admin/auth/logout')
    } catch {
      // Ignore — session may already be invalid
    } finally {
      user.value = null
      pendingChallenge.value = null
    }
  }

  return {
    user, loading, pendingChallenge,
    isAuthenticated, isOwner, fullName, permissionCodes,
    can, login, verify2FA, cancelChallenge, fetchMe, logout, clearAuth,
  }
}, {
  // v3 utilise `paths` (et non `pick` qui n'apparait qu'en v4) pour
  // selectionner les cles a persister. Comportement identique a pick.
  persist: { paths: ['user'] },
})
